import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TrainSearchResponse } from "@core/models/trains/train-search-response";
import { environment } from "@env/environment";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { TrainSearchRequest } from "../models/trains/train-search-request";
import { TokenService } from "./token.service";
import { TrainsInfo } from "@core/models/trainsInfo";
import { TranslateService } from "@ngx-translate/core";

@Injectable({ providedIn: 'root' })
export class TrainService {
  private trainsInfoSubject = new BehaviorSubject<TrainsInfo | null>(null);
  private correlationSubject = new BehaviorSubject<string[]>([]);
  correlation$ = this.correlationSubject.asObservable();
  constructor(private http: HttpClient, private tokenService: TokenService, private translate: TranslateService) {
    /* this.trainsInfoSubject.subscribe(v => {
      console.trace('TRAINS INFO UPDATE 👉', v);
    }); */
  }

  selectTrain(
    train: any,
    type: string,
    trainsInfo: any,
    selectedAlternatives: any[] = []
  ) {
    if (!selectedAlternatives || selectedAlternatives.length < 1) {
      selectedAlternatives = train.sections.map(
        (s) => s.alternatives[0].alternativeId
      );
    }

    const selectedTrains = trainsInfo.selectedTrains
      ? { ...trainsInfo.selectedTrains }
      : {};

    const updateAlternatives = (
      currentAlternatives: any[] = [],
      previousTrain: any
    ) => {
      // Si cambia el tren, sustituimos todas las alternativas
      if (!previousTrain || previousTrain.trainId !== train.trainId) {
        return selectedAlternatives;
      }

      // Si es el mismo tren, sustituimos solo las de la sección actual
      const remainingAlternatives = currentAlternatives.filter(
        altId =>
          !train.sections.flatMap((s) => s?.alternatives.some(
            a => a.alternativeId === altId
          ))
      );

      return [...remainingAlternatives, ...selectedAlternatives];
    };

    if (type === 'outward') {
      selectedTrains.selectedOutwardAlternatives = updateAlternatives(
        selectedTrains.selectedOutwardAlternatives,
        selectedTrains.selectedOutwardTrain
      );

      selectedTrains.selectedOutwardTrain = train;
    } else if (type === 'return') {
      selectedTrains.selectedReturnAlternatives = updateAlternatives(
        selectedTrains.selectedReturnAlternatives,
        selectedTrains.selectedReturnTrain
      );

      selectedTrains.selectedReturnTrain = train;
    }

    this.setTrainsInfoPartial({
      selectedTrains
    });
  }

  addCorrelation(value: string) {
    const current = this.correlationSubject.value;
    if (!current.includes(value)) {
      this.correlationSubject.next([...current, value]);
    }
  }

  removeCorrelation(value: string) {
    const current = this.correlationSubject.value;
    const updated = current.filter(item => item !== value);
    this.correlationSubject.next(updated);
  }

  existsCorrelation(value: string): boolean {
    return this.correlationSubject.value.includes(value);
  }

  calculateFees(data: TrainsInfo) {
    let totalFees = 0;
    totalFees += data.selectedTrains.selectedOutwardTrain.finalCommision * 1;
    if (data.selectedTrains.selectedReturnAlternatives) {
      totalFees += data.selectedTrains.selectedReturnTrain.finalCommision * 1;
    }
    return totalFees.toFixed(2);
  }

  getSeatPassengerPrice(seat: any): number {
    const passengerId = `${seat?.passengerId || ""}`;
    const passengerPrice = Array.isArray(seat?.passengersPrice)
      ? seat.passengersPrice.find((price) => `${price?.passengerId}` === passengerId) || seat.passengersPrice[0]
      : null;

    return Number(passengerPrice?.price ?? seat?.price ?? 0) || 0;
  }

  getSeatsTotal(seats: any[] = []): number {
    return Array.isArray(seats)
      ? seats.reduce((sum, seat) => sum + this.getSeatPassengerPrice(seat), 0)
      : 0;
  }

  calculateExtras(data: TrainsInfo) {
    let totalExtras = 0;
    if (data.selectedSeats && data.selectedSeats.outward) {
      totalExtras += this.getSeatsTotal(data.selectedSeats.outward);
      if (data.selectedSeats.return) {
        totalExtras += this.getSeatsTotal(data.selectedSeats.return);
      }
    }
    if (data.selectedExtras) {
      totalExtras += data.selectedExtras.outward.reduce((sum, extra) => sum + extra.price * extra.quantity, 0);
      if (data.selectedExtras.return) {
        totalExtras += data.selectedExtras.return.reduce((sum, extra) => sum + extra.price * extra.quantity, 0);
      }
    }
    return totalExtras.toFixed(2);
  }

  calculateProviderPrice(data: TrainsInfo, type?: string) {
    const outwardAlternatives = data.selectedTrains.selectedOutwardTrain.sections.flatMap((s) => s.alternatives.filter((a) => data.selectedTrains.selectedOutwardAlternatives.includes(a.alternativeId)))
    let totalFees: number = 0;
    outwardAlternatives.forEach((a) => totalFees += a.price * 1)
    if (type === 'outward') return totalFees
    if (data.selectedTrains.selectedReturnAlternatives) {
      if (type === 'return') totalFees = 0
      const returnAlternatives = data.selectedTrains.selectedReturnTrain.sections.flatMap((s) => s.alternatives.filter((a) => data.selectedTrains.selectedReturnAlternatives.includes(a.alternativeId)))
      returnAlternatives.forEach((a) => totalFees += a.price * 1)
      if (type === 'return') return totalFees
    }
    return totalFees * 1;
  }

  calculateBasePrice(data: TrainsInfo): number {
    const providerPrice = this.calculateProviderPrice(data, 'total');
    return data.company.showCommissions
      ? providerPrice
      : providerPrice + Number(this.calculateFees(data));
  }

  calculateFinalPrice(data: TrainsInfo, type?: string) {
    let totalFees: number = this.calculateProviderPrice(data, 'total') * 1;
    totalFees += data.selectedTrains.selectedOutwardTrain.finalCommision
    if (type === 'outward') return totalFees
    if (data.selectedTrains.selectedReturnAlternatives) {
      totalFees += data.selectedTrains.selectedReturnTrain.finalCommision
      if (type === 'return') return totalFees
    }
    totalFees += parseFloat(this.calculateExtras(data))
    return totalFees * 1;
  }

  totalPrice(data: TrainsInfo) {
    let totalPrice = 0;
    const price: any = {
      outward: {
        finalPrice: this.calculateProviderPrice(data, 'outward') * 1 + data.selectedTrains.selectedOutwardTrain.finalCommision * 1,
        iva: data.selectedTrains.selectedOutwardTrain.iva * 1,
        finalCommision: data.selectedTrains.selectedOutwardTrain.finalCommision * 1,
        providerPrice: this.calculateProviderPrice(data, 'outward') * 1,
        extras: 0,
        seats: 0,
      }
    };
    if (data.selectedExtras?.outward && Array.isArray(data.selectedExtras?.outward)) {
      const totalExtras = data.selectedExtras?.outward.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
      price.outward.extras = totalExtras || 0;
    }
    if (data.selectedSeats?.outward && Array.isArray(data.selectedSeats?.outward)) {
      const totalSeats = this.getSeatsTotal(data.selectedSeats.outward);
      price.outward.seats = totalSeats || 0;
    }
    totalPrice += price.outward.finalPrice + price.outward.extras + price.outward.seats;
    if (data.selectedTrains.selectedReturnAlternatives) {
      price.return = {
        finalPrice: this.calculateProviderPrice(data, 'return') * 1 + data.selectedTrains.selectedReturnTrain.finalCommision * 1,
        iva: data.selectedTrains.selectedReturnTrain.iva * 1,
        finalCommision: data.selectedTrains.selectedReturnTrain.finalCommision * 1,
        providerPrice: this.calculateProviderPrice(data, 'return') * 1,
        extras: 0,
        seats: 0,
      }
      if (data.selectedExtras?.return && Array.isArray(data.selectedExtras?.return)) {
        const totalExtras = data.selectedExtras?.return.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
        price.return.extras = totalExtras || 0;
      }
      if (data.selectedSeats?.return && Array.isArray(data.selectedSeats?.return)) {
        const totalSeats = this.getSeatsTotal(data.selectedSeats.return);
        price.return.seats = totalSeats || 0;
      }
      totalPrice += price.return.finalPrice + price.return.extras + price.return.seats;
    }
    return totalPrice.toFixed(2)
  }

  setTrainsInfo(data: TrainsInfo | null) {
    this.trainsInfoSubject.next(data);
  }

  clearTrainsInfo() {
    console.log('CLEARING');
    this.trainsInfoSubject.next(null);
  }

  setTrainsInfoPartial(patch: Partial<TrainsInfo>) {
    const current = this.trainsInfoSubject.value;

    if (!current) {
      // si no hay estado, no mezclar basura vieja
      this.trainsInfoSubject.next(patch as TrainsInfo);
      return;
    }

    this.trainsInfoSubject.next({
      ...current,
      ...patch
    });
  }

  getTrainsInfo$(): Observable<TrainsInfo | null> {
    return this.trainsInfoSubject.asObservable();
  }

  checkOriginOrDestinationMatch(trainsInfo) {
    if (
      trainsInfo.formSearchTrain.value.origin ===
      trainsInfo.formSearchTrain.value.destination
    ) {
      return true;
    } else {
      return false;
    }
  }

  getCarrierLogo(carrier: string) {
    switch (carrier?.toLowerCase()) {
      case "ouigo":
        return "assets/img/svg/train/ouigo.svg";
      case "iryo":
      case "ilsa":
        return "assets/img/svg/train/iryo.png";
      case "renfe":
      default:
        return "assets/img/svg/train/renfe.svg";
    }
  }

  getFlexibilityString(flexibility: string) {
    this.translate
      .get(["train.non-flexi", "train.semi-flexi", "train.flexi"])
      .subscribe((result) => {
        switch (flexibility.toLowerCase()) {
          case "flexi":
            return result["train.flexi"];
          case "semiflexi":
            return result["train.semi-flexi"];
          case "nonflexi":
          default:
            return result["train.non-flexi"];
        }
      })
  }

  getDuration(train: any): string {
    const date1 = new Date(train.arriveDate);
    const date2 = new Date(train.departDate);

    const diffMs = Math.abs(date1.getTime() - date2.getTime()); // diferencia en milisegundos

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMin = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs} h ${diffMin} min`;
  }

  getSectionDuration(section: any): string {
    const date1 = new Date(section.arriveDate);
    const date2 = new Date(section.departDate);

    const diffMs = Math.abs(date1.getTime() - date2.getTime()); // diferencia en milisegundos

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMin = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs} h ${diffMin} min`;
  }

  postTrainRequest(trainReq: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/request`,
      trainReq,
      { headers: reqHeaders }
    );
  }
  postCreateTrain(form: FormData, requestUuid?: string) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    const url = requestUuid
      ? `${environment.baseUrlApi}/train?requestUuid=${requestUuid}`
      : `${environment.baseUrlApi}/train`;
    return this.http.post<any>(url, form, {
      headers: reqHeaders,
    });
  }
  getUserTrain() {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http
      .get<any>(`${environment.baseUrlApi}/train`, { headers: reqHeaders })
      .pipe(map((res: any) => res.docs ? res.docs : res));
  }

  updateTrainStatus(trainUuid: string) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.put<any>(
      `${environment.baseUrlApi}/train/status/${trainUuid}`, {},
      { headers: reqHeaders }
    );
  }

  editTrain(trainId: string, body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/update/${trainId}`,
      body,
      { headers: reqHeaders }
    );
  }
  changeStatusAuthorization(body: any, uuid: string, type: string) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.put<any>(
      `${environment.baseUrlApi}/authorization/${uuid}/train/${type}`,
      body,
      { headers: reqHeaders }
    );
  }

  searchTrainsOutward(
    request: TrainSearchRequest
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/search-outward`,
      request,
      { headers: reqHeaders }
    );
  }

  searchTrainsReturn(
    request: TrainSearchRequest
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/search-return`,
      request,
      { headers: reqHeaders }
    );
  }

  selectTrains(
    request: TrainSearchRequest
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/select`,
      request,
      { headers: reqHeaders }
    );
  }

  reserveTrains(request
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/reserve`,
      request,
      { headers: reqHeaders }
    );
  }

  createReservation(request
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/create-reservation`,
      request,
      { headers: reqHeaders }
    );
  }

  getStations() {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.get(
      `${environment.baseUrlApi}/train/resources/stations`,
      { headers: reqHeaders }
    );
  }

  getCardTypes() {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.get(
      `${environment.baseUrlApi}/train/resources/card-types`,
      { headers: reqHeaders }
    );
  }

  selectExtrasTrains(request
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/select-extras`,
      request,
      { headers: reqHeaders }
    );
  }

  selectSeatsTrains(request
  ): Observable<TrainSearchResponse> {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<TrainSearchResponse>(
      `${environment.baseUrlApi}/train/select-seats`,
      request,
      { headers: reqHeaders }
    );
  }

  postTrainBookTrainMore(body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(`${environment.baseUrlApi}/train/book`, body, {
      headers: reqHeaders,
    });
  }

  postTrainBuyTrainMore(body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(`${environment.baseUrlApi}/train/buy`, body, {
      headers: reqHeaders,
    });
  }

  cancelTicketsPrice(body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/cancelTicketsPrice`,
      body,
      { headers: reqHeaders }
    );
  }

  cancelTicketsPartial(body: any, uuid: string) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/cancelTicketsPartial/${uuid}`,
      body,
      { headers: reqHeaders }
    );
  }

  cancelTickets(body: any, uuid: string) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/cancelTickets/${uuid}`,
      body,
      { headers: reqHeaders }
    );
  }

  cancelReservation(trainUuid: string, body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/cancel-reservation/${trainUuid}`,
      body,
      { headers: reqHeaders }
    );
  }

  getTrainsComplements(body: any): Observable<any> {
    // apiSession
    // outwardId
    // retId - not required
    // classCodeOutward
    // classSalesGroupCoodeOutward
    // classCodeReturn - not required
    // classSalesGroupCodeReturn - not required
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/getTrainsComplements`,
      body,
      { headers: reqHeaders }
    );
  }

  getGraph(body: any): Observable<any> {
    // apiSession
    // outwardId
    // retId - not required
    // classCodeOutward
    // classSalesGroupCoodeOutward
    // classCodeReturn - not required
    // classSalesGroupCodeReturn - not required
    // userUuid - not required
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/getGraph`,
      body,
      { headers: reqHeaders }
    );
  }

  bookTrainsGraph(body: any) {
    // apiSession
    // outwardId
    // retId - not required
    // classCodeOutward
    // classSalesGroupCoodeOutward
    // classCodeReturn - not required
    // classSalesGroupCodeReturn - not required
    // outwardSeats
    // returnSeats
    // userUuid - not required
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/bookTrainsGraph`,
      body,
      { headers: reqHeaders }
    );
  }

  createLetItGo(body: any) {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.post<any>(
      `${environment.baseUrlApi}/train/letitgo`,
      body,
      { headers: reqHeaders }
    );
  }

  updateStaticData() {
    const token = this.tokenService.getToken();
    const reqHeaders = {
      Authorization: `JWT ${token}`,
    };
    return this.http.get(
      `${environment.baseUrlApi}/train/update/static-data`,
      { headers: reqHeaders }
    );
  }
}
